'use client';

import { useState, useRef } from 'react';
import { motion } from 'motion/react';
import { X, Check, Play } from 'lucide-react';

const BEFORE_VIDEO =
  'https://gw-ai-social-media.b-cdn.net/VideoMule/VM-Homepage/vm-before-video.webm';
const AFTER_VIDEO =
  'https://gw-ai-social-media.b-cdn.net/VideoMule/VM-Homepage/Videomule%20demo%20for%20client.mp4#t=0.1';

const beforePoints = [
  {
    title: 'No Audio Explanation',
    desc: 'Your screen recording has no narration to explain the steps.'
  },
  {
    title: 'Low Engagement',
    desc: 'Viewers quickly lose interest without clear guidance.'
  },
  {
    title: 'Manual Script & Voiceover',
    desc: 'You must write scripts and record voiceovers separately.'
  },
  {
    title: 'Slow Production Workflow',
    desc: 'Recording, editing, and adding voiceover can take hours.'
  }
];

const afterPoints = [
  {
    title: 'Automatic Voiceover & Sync',
    desc: 'AI generates a script and perfectly narrates every step.'
  },
  {
    title: 'Higher Viewer Engagement',
    desc: 'Professional narration keeps viewers watching.'
  },
  {
    title: 'Ready in Minutes',
    desc: 'VideoMule turns your recording into a tutorial video instantly.'
  },
  {
    title: 'All-in-One Workflow',
    desc: 'Script generation, voiceover, captions, and editing in one place.'
  }
];

function BrowserFrame({
  title,
  accent,
  src,
  showControls
}: {
  title: string;
  accent: 'red' | 'green';
  src: string;
  showControls?: boolean;
}) {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const toggle = () => {
    if (!videoRef.current) return;
    if (playing) videoRef.current.pause();
    else videoRef.current.play();
    setPlaying(!playing);
  };

  return (
    <div className="rounded-xl overflow-hidden border border-border shadow-sm bg-card">
      <div className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-muted/60 border-b border-border">
        <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-destructive/70" />
        <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-400" />
        <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-primary/70" />
        <span
          className={`ml-auto mr-auto text-xs sm:text-sm font-semibold ${
            accent === 'red' ? 'text-foreground' : 'text-primary'
          }`}
        >
          {title}
        </span>
      </div>
      <div className={`relative ${!showControls ? 'cursor-pointer' : ''}`} onClick={!showControls ? toggle : undefined}>
        <video
          ref={videoRef}
          src={src}
          className="w-full aspect-video bg-black object-contain"
          playsInline
          preload="metadata"
          muted={!showControls}
          loop
          controls={showControls}
        />
        {!showControls && !playing && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border-[3px] border-white/80 bg-primary/80 flex items-center justify-center shadow-xl backdrop-blur-sm">
              <Play
                className="h-5 w-5 sm:h-7 sm:w-7 ml-0.5 sm:ml-1 text-primary-foreground"
                fill="currentColor"
              />
            </div>
          </div>
        )}
        {accent === 'red' && (
          <>
            <div className="absolute top-2 right-2 sm:top-3 sm:right-3 w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-destructive flex items-center justify-center shadow-md">
              <X className="h-4 w-4 sm:h-5 sm:w-5 text-primary-foreground" />
            </div>
            <div className="absolute top-2 left-2 sm:top-3 sm:left-3 w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-foreground/60 flex items-center justify-center backdrop-blur-sm">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon
                  points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"
                  fill="white"
                />
                <line x1="23" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="23" y2="15" />
              </svg>
            </div>
          </>
        )}
        {accent === 'green' && (
          <div className="absolute top-2 right-2 sm:top-3 sm:right-3 w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-primary flex items-center justify-center shadow-md">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              className="text-primary-foreground sm:w-5 sm:h-5"
            >
              <rect x="2" y="8" width="2.5" height="8" rx="1.25" fill="currentColor">
                <animate attributeName="height" values="8;14;4;10;8" dur="1.2s" repeatCount="indefinite" />
                <animate attributeName="y" values="8;5;10;7;8" dur="1.2s" repeatCount="indefinite" />
              </rect>
              <rect x="6.5" y="4" width="2.5" height="16" rx="1.25" fill="currentColor">
                <animate attributeName="height" values="16;6;12;18;16" dur="1s" repeatCount="indefinite" />
                <animate attributeName="y" values="4;9;6;3;4" dur="1s" repeatCount="indefinite" />
              </rect>
              <rect x="11" y="7" width="2.5" height="10" rx="1.25" fill="currentColor">
                <animate attributeName="height" values="10;18;6;14;10" dur="1.4s" repeatCount="indefinite" />
                <animate attributeName="y" values="7;3;9;5;7" dur="1.4s" repeatCount="indefinite" />
              </rect>
              <rect x="15.5" y="3" width="2.5" height="18" rx="1.25" fill="currentColor">
                <animate attributeName="height" values="18;8;14;4;18" dur="0.9s" repeatCount="indefinite" />
                <animate attributeName="y" values="3;8;5;10;3" dur="0.9s" repeatCount="indefinite" />
              </rect>
              <rect x="20" y="6" width="2.5" height="12" rx="1.25" fill="currentColor">
                <animate attributeName="height" values="12;4;16;8;12" dur="1.1s" repeatCount="indefinite" />
                <animate attributeName="y" values="6;10;4;8;6" dur="1.1s" repeatCount="indefinite" />
              </rect>
            </svg>
          </div>
        )}
      </div>
    </div>
  );
}

export default function BeforeAfter() {
  return (
    <section className="py-14 sm:py-20 md:py-24 relative overflow-hidden bg-section-green">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <motion.div
          className="text-center mb-8 sm:mb-12 md:mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-flex items-center rounded-full border border-primary/20 px-3 py-1 text-xs font-medium text-primary mb-4 bg-videomule-light-green dark:bg-primary/10">
            See the Difference
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-foreground mb-4">
            Before vs After VideoMule
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            Why struggle with traditional methods when you can automate the
            entire process? See the impact VideoMule creates.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-5 sm:gap-8 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            whileHover={{ y: -4 }}
            className="rounded-2xl border border-border bg-card p-5 sm:p-6 md:p-8 shadow-sm flex flex-col gap-5 sm:gap-7"
          >
            <div className="flex justify-center">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-destructive/10 text-destructive px-3 sm:px-4 py-1 sm:py-1.5 text-xs sm:text-sm font-semibold">
                <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                Before VideoMule
              </span>
            </div>
            <BrowserFrame
              title="Silent Screen Recording"
              accent="red"
              src={BEFORE_VIDEO}
            />
            <ul className="space-y-4 sm:space-y-5 pt-1">
              {beforePoints.map((item) => (
                <li
                  key={item.title}
                  className="flex items-start gap-2.5 sm:gap-3"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-destructive shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-sm sm:text-base text-foreground">
                      {item.title}
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {item.desc}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ y: -4 }}
            className="rounded-2xl border border-border bg-card p-5 sm:p-6 md:p-8 shadow-sm flex flex-col gap-5 sm:gap-7"
          >
            <div className="flex justify-center">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 sm:px-4 py-1 sm:py-1.5 text-xs sm:text-sm font-semibold">
                <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                After VideoMule
              </span>
            </div>
            <BrowserFrame
              title="Professional AI Voiceover"
              accent="green"
              src={AFTER_VIDEO}
              showControls
            />
            <ul className="space-y-4 sm:space-y-5 pt-1">
              {afterPoints.map((item) => (
                <li
                  key={item.title}
                  className="flex items-start gap-2.5 sm:gap-3"
                >
                  <Check className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-sm sm:text-base text-foreground">
                      {item.title}
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {item.desc}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
